import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

export interface Tour {
  id: string;
  title: string;
  slug: string;
  duration: string;
  spots: number;
  category: string;
  group_type: string;
  price: number;
  image: string;
  created_at: string;
  style: string;
}

interface TourRow extends Tour {
  title_vi: string | null;
  duration_vi: string | null;
  category_vi: string | null;
  group_type_vi: string | null;
}

function toStyle(category: string | null): string {
  const c = (category || '').toLowerCase();
  if (c.includes('multi-day')) return 'multi-day';
  if (c.includes('cruise')) return 'cruise';
  if (c.includes('day')) return 'day';
  return 'other';
}

export function useTours() {
  const [rows, setRows] = useState<TourRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { language } = useLanguage();

  const fetchTours = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: err } = await supabase
        .from('vietnamtravel_tours')
        .select('id, title, title_vi, slug, duration, duration_vi, spots, category, category_vi, group_type, group_type_vi, price, image, created_at')
        .order('created_at', { ascending: false });

      if (err) throw err;
      setRows((data || []) as TourRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tours');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTours();
  }, [fetchTours]);

  const tours: Tour[] = rows.map((r) => ({
    id: r.id,
    title: loc(language, r.title, r.title_vi),
    slug: r.slug,
    duration: loc(language, r.duration, r.duration_vi),
    spots: r.spots,
    category: loc(language, r.category, r.category_vi),
    group_type: loc(language, r.group_type, r.group_type_vi),
    price: r.price,
    image: r.image,
    created_at: r.created_at,
    style: toStyle(r.category),
  }));

  return { tours, loading, error, refetch: fetchTours };
}